import * as React from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { ButtonWidget } from "@/components/widgets/ButtonWidget";
import { FlowWidget, FlowWidgetProps } from "@/components/widgets/FlowWidget";
import { ChatHistory } from "./chat-history";
import { ChatContext } from "@/context/chat";
import { UIContext } from "@/context/ui";
import { ChevronUp, ChevronDown, Plus } from "lucide-react";
import { SpecialCTA } from "./cta-special";
import { MicButton } from "./MicButton";
import { Input } from "@/components/ui/input";
import { ProjectContext, ProjectContextType } from "@/context/project";

interface BaseWidget {
  hidden?: boolean;
}

interface ButtonWidgetProps extends BaseWidget {
  label: string;
  action: string;
}

interface FlowWidgetType extends BaseWidget {
  type: "flow";
  props: FlowWidgetProps;
}

interface ButtonWidgetType extends BaseWidget {
  type: "button";
  props: ButtonWidgetProps;
}

type Widget = FlowWidgetType | ButtonWidgetType;

interface ChatMessage {
  sender: "user" | "bot";
  text: string;
  widgets?: Widget[];
}

interface HistoryItem {
  role: string;
  parts: { text: string }[];
}

export const ChatPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [pinnedFlowWidgetState, setPinnedFlowWidgetState] =
    React.useState<{ widget: FlowWidgetType; chatId?: string } | null>(null);

  const pinnedFlowWidget = React.useMemo(() => {
    if (!pinnedFlowWidgetState) return null;
    if (!chatId) return pinnedFlowWidgetState.widget;
    return pinnedFlowWidgetState.chatId === chatId
      ? pinnedFlowWidgetState.widget
      : null;
  }, [pinnedFlowWidgetState, chatId]);
  const currentArchitectureRef = React.useRef<{
    apps: Record<string, unknown>;
    edges: unknown[];
  } | null>(null);

  React.useEffect(() => {
    if (pinnedFlowWidget) {
      currentArchitectureRef.current = {
        apps: pinnedFlowWidget.props.apps || {},
        edges: pinnedFlowWidget.props.edges || [],
      };
    }
  }, [pinnedFlowWidget]);
  const [input, setInput] = React.useState(() => {
    const searchParams = new URLSearchParams(location.search);
    const prompt = localStorage.getItem("buzooka_prompt");

    return prompt || searchParams.get("prompt") || "";
  });

  const [isConnecting, setIsConnecting] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(false);
  const uiContext = React.useContext(UIContext);

  if (!uiContext) {
    throw new Error("ChatPage must be used within a UIProvider");
  }

  const { rightSidebar, setRightSidebar, setChatTitle } = uiContext;
  const initialOpenSidebar = location.state?.openSidebar;


  React.useEffect(() => {
    if (initialOpenSidebar) {
      setRightSidebar(true);
    } else {
      setRightSidebar(false);
    }
  }, [initialOpenSidebar, setRightSidebar]);

  const { projectsMap, setProjectsMap } = React.useContext<ProjectContextType>(ProjectContext);
  const [projectName, setProjectName] = React.useState("");
  const [domainName, setDomainName] = React.useState("");
  const [creatingProject, setCreatingProject] = React.useState(false);
  const [createProjectError, setCreateProjectError] = React.useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [chatProjects, setChatProjects] = React.useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = React.useState(false);

  const handleCreateProject = async () => {
    if (!projectName.trim() || !domainName.trim()) {
      setCreateProjectError("Project Name and Domain Name are required.");
      return;
    }
    setCreateProjectError(null);
    setCreatingProject(true);

    const currentArch = currentArchitectureRef.current;
    if (!currentArch) {
      setCreateProjectError("No architecture to create.");
      setCreatingProject(false);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apps = (currentArch.apps || {}) as Record<string, any>;
    const edges = (currentArch.edges || []) as unknown[];

    const payloadApps: Record<
      string,
      { name: string; template: string; config: Record<string, unknown> }
    > = {};

    Object.values(apps).forEach((app) => {
      payloadApps[app.id] = {
        name: app.name,
        template: app.template,
        config: app.config,
      };
    });

    const payload = {
      name: projectName,
      domainName,
      apps: payloadApps,
      edges,
      chatId,
    };

    try {

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/project`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          credentials: "include",
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data?.data?.id) {
          setProjectsMap?.({
            ...projectsMap,
            [data?.data?.id]: data?.data,
          });
          navigate(`/projects/${data?.data?.id}`);
        }
      } else {
        const responseData = await response.json();
        setCreateProjectError(responseData?.error?.message || "Failed to create project");
      }
    } catch {
      setCreateProjectError("An unexpected error occurred.");
    } finally {
      setCreatingProject(false);
    }
  };

  const handleButtonClick = (
    messageIndex: number,
    widgetIndex: number,
    action: string,
    label: string,
  ) => {
    setMessages((prevMessages) => {
      const newMessages = [...prevMessages];
      const message = newMessages[messageIndex];
      if (message?.widgets?.[widgetIndex]) {
        // Hide all buttons in the same message
        message.widgets.forEach((widget) => {
          if (widget.type === "button") {
            widget.props.hidden = true;
          }
        });
      }
      return newMessages;
    });
    handleSendMessage(label, {
      displayAsUserMessage: true,
      displayText: label,
    });
  };

  const handleSendMessage = React.useCallback(
    async (
      message: string,
      options?: { displayAsUserMessage?: boolean; displayText?: string },
    ) => {
      if (!message.trim()) return;

      setIsConnecting(true); // Disable input and button immediately

      const displayAsUserMessage = options?.displayAsUserMessage ?? true;
      const textToDisplay = options?.displayText ?? message;

      if (displayAsUserMessage) {
        const userMessage: ChatMessage = {
          sender: "user",
          text: textToDisplay,
        };
        setMessages((prev) => [...prev, userMessage]);
      }
      const currentInput = message;
      setInput("");

      let effectiveChatId = chatId;

      // If there's no chatId, this is the first message of a new chat.
      // We need to create the chat first.
      if (!effectiveChatId) {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/api/chats`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
            },
          );

          if (!response.ok) {
            throw new Error("Failed to create chat");
          }

          const data = await response.json();
          effectiveChatId = data.chatId;
          // Navigate to the new chat URL, but without the initialPrompt in state,
          // because we are going to handle sending the message right now.
          navigate(`/chat/${effectiveChatId}`, { replace: true });
        } catch (error) {
          console.error("Error creating chat:", error);
          const errorMessage: ChatMessage = {
            sender: "bot",
            text: "Sorry, I couldn't start a new chat. Please try again.",
          };
          setMessages((prev) => [...prev, errorMessage]);
          return;
        } finally {
          // isConnecting will be set to false in the outer finally block
        }
      }

      if (!effectiveChatId) {
        return; // Should not happen if the above logic is correct
      }

      // Now, send the message to the chat API (for both new and existing chats).
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/api/chat`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              message: currentInput,
              chatId: effectiveChatId,
              architecture: currentArchitectureRef.current ?? { apps: {}, edges: []},
            }),
            credentials: "include",
          },
        );

        if (!response.body) {
          throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        const botMessage: ChatMessage = {
          sender: "bot",
          text: "",
          widgets: [],
        };
        setMessages((prev) => [...prev, botMessage]);
        localStorage.removeItem("buzooka_prompt");

        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");

          buffer = parts.pop() || "";

          for (const part of parts) {
            if (part.trim() === "") continue;

            const lines = part.split("\n");
            let event = "";
            let data = "";

            for (const line of lines) {
              if (line.startsWith("event: ")) {
                event = line.substring(7).trim();
              } else if (line.startsWith("data: ")) {
                data = line.substring(6).trim();
              }
            }

            if (event === "end") {
              return;
            }

            if (event === "chunk" && data) {
              try {
                const parsedData = JSON.parse(data);

                if (parsedData.widgetJson?.type === "flow") {
                  setPinnedFlowWidgetState({
                    widget: parsedData.widgetJson,
                    chatId: effectiveChatId,
                  });
                  if (parsedData.widgetJson.props?.name) {
                    setProjectName(parsedData.widgetJson.props.name);
                  }

                }

                setMessages((prevMessages) => {
                  const newMessages = [...prevMessages];
                  const lastMessage = newMessages[newMessages.length - 1];

                  if (lastMessage && lastMessage.sender === "bot") {
                    const updatedMessage = { ...lastMessage };
                    if (
                      parsedData.widgetJson &&
                      parsedData.widgetJson.type !== "flow"
                    ) {
                      updatedMessage.widgets = [
                        ...(updatedMessage.widgets || []),
                        parsedData.widgetJson,
                      ];
                    }
                    if (parsedData.explanation) {
                      updatedMessage.text =
                        updatedMessage.text + parsedData.explanation;
                    }
                    newMessages[newMessages.length - 1] = updatedMessage;
                    return newMessages;
                  }
                  return prevMessages;
                });
              } catch (e) {
                console.error("Error parsing SSE data chunk:", e);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error sending message to AI:", error);
        const errorMessage: ChatMessage = {
          sender: "bot",
          text: "Sorry, I encountered an error.",
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsConnecting(false);
      }
    },
    [chatId, navigate],
  );

  React.useEffect(() => {
    // Reset state when switching chats
    setMessages([]);
    setPinnedFlowWidgetState(null);
    currentArchitectureRef.current = null;
    setChatProjects([]);
    setProjectName("");
    setDomainName("");
    setShowCreateForm(false);
    setCreateProjectError(null);
    setChatTitle("");

    if (!chatId) return;

    const loadChatData = async () => {
      setIsConnecting(true);
      let historyWidgetFound = false;

      // 1. Fetch Chat History
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/api/chats/${chatId}`,
          { credentials: "include" }
        );

        if (response.ok) {
          const data = await response.json();
          setChatTitle(data.title || "New Chat");
          const history = data.history || [];
          
          const messagesWithAllWidgets: ChatMessage[] = history.map(
            (item: HistoryItem) => {
              const messageText = item.parts[0].text;
              const widgetRegex = /```widget\n([\s\S]*?)\n```/g;
              const widgets: Widget[] = [];
              let text = messageText;
              let match;

              while ((match = widgetRegex.exec(messageText)) !== null) {
                try {
                  widgets.push(JSON.parse(match[1]));
                } catch (e) {
                  console.error("Error parsing widget from history", e);
                }
              }
              text = text.replace(widgetRegex, "").trim();

              return {
                sender: item.role === "user" ? "user" : "bot",
                text,
                widgets,
              };
            }
          );

          const lastFlowWidget = messagesWithAllWidgets
            .flatMap((m) => m.widgets ?? [])
            .filter((w) => w.type === "flow")
            .pop();

          if (lastFlowWidget) {
            setPinnedFlowWidgetState({ widget: lastFlowWidget, chatId });
            historyWidgetFound = true;
            if (lastFlowWidget.props?.name) {
              setProjectName(lastFlowWidget.props.name);
            }

          }

          const formattedMessages = messagesWithAllWidgets.map((message) => ({
            ...message,
            widgets: message.widgets?.filter((w) => w.type !== "flow"),
          }));

          // Hide buttons in older messages
          if (formattedMessages.length > 0) {
            const lastMessageIndex = formattedMessages.length - 1;
            const lastMessage = formattedMessages[lastMessageIndex];
            formattedMessages.forEach((message, index) => {
              if (message.widgets) {
                const isLastBotMessage =
                  index === lastMessageIndex && lastMessage.sender === "bot";
                if (!isLastBotMessage) {
                  message.widgets.forEach((widget) => {
                    if (widget.type === "button") {
                      widget.props.hidden = true;
                    }
                  });
                }
              }
            });
            setMessages(formattedMessages);
          }
        } else {
          setMessages([{ sender: "bot", text: "Could not load chat history." }]);
        }
      } catch (error) {
        console.error("Error fetching chat history:", error);
        setMessages([{ sender: "bot", text: "Error loading chat history." }]);
      }

      // 2. Fetch Chat Projects
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/api/chats/${chatId}/projects`,
          { credentials: "include" }
        );

        if (response.ok) {
          const projects = await response.json();
          if (Array.isArray(projects) && projects.length > 0) {
            setChatProjects(projects);

            // Only fallback to project architecture if NO history architecture was found
            if (!historyWidgetFound) {
              const project = projects[projects.length - 1];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const appsRecord = (project.apps as any[]).reduce((acc, app) => {
                acc[app.id] = app;
                return acc;
              }, {} as Record<string, unknown>);

              setPinnedFlowWidgetState({
                widget: {
                  type: "flow",
                  props: {
                    apps: appsRecord,
                    edges: project.edges,
                    name: project.name,
                    domainName: project.domainName,
                  },
                },
                chatId,
              });

              if (project.name) setProjectName(project.name);

            }
          } else {
            setChatProjects([]);
          }
        }
      } catch (error) {
        console.error("Error fetching chat projects:", error);
      } finally {
        setIsConnecting(false);
      }
    };

    loadChatData();
  }, [chatId]);

  const initialPrompt = location.state?.initialPrompt;
  const initialPromptSent = React.useRef(false);

  React.useEffect(() => {
    if (initialPrompt && !initialPromptSent.current) {
      initialPromptSent.current = true;
      handleSendMessage(initialPrompt, { displayAsUserMessage: false });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [initialPrompt, handleSendMessage, navigate]);

  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isMinimized]);

  const lastMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;

  const lastUserMessage = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === "user") return messages[i];
    }
    return null;
  }, [messages]);

  const isBotResponding = !!(
    lastMessage?.sender === "bot" &&
    (lastMessage.text?.trim() || lastMessage.widgets?.length)
  );

  const showLastMessageInMinimized =
    (lastMessage?.sender === "bot" &&
      lastMessage.widgets?.some((w) => w.type === "button")) ||
    (isConnecting && !isBotResponding);

  return (
    <ChatContext.Provider value={{ handleSendMessage }}>
      <div className="flex h-full bg-background">
        <main className="relative flex-1">
          <FlowWidget
            key={chatId}
            className="h-full w-full"
            {...pinnedFlowWidget?.props}
            apps={pinnedFlowWidget?.props?.apps || {}}
            edges={pinnedFlowWidget?.props?.edges || []}
            mode="create"
            hideCreateControls={true}
            onChange={(state) => {
              currentArchitectureRef.current = state;
            }}
          />
          <div
            className={`absolute bottom-4 left-1/2 flex ${
              isMinimized ? "max-h-[33vh]" : "max-h-[70vh]"
            } group w-full max-w-lg -translate-x-1/2 flex-col gap-2`}
          >
            <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-tr from-cyan-500 to-pink-500 opacity-20 blur transition duration-500 group-hover:opacity-40"></div>
            <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-bl from-cyan-300 to-pink-300 opacity-0 blur transition duration-500 group-focus-within:opacity-100"></div>
            <div className="z-1 relative flex min-h-0 w-full flex-col overflow-hidden rounded-2xl border bg-background shadow-xl">
              {!isMinimized && messages.length > 0 && (
                <ScrollArea type="scroll" className="flex-1 overflow-y-auto p-2 [-ms-overflow-style:none] [scrollbar-width:none] hover:[scrollbar-width:thin] [&::-webkit-scrollbar]:w-0 hover:[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-track]:bg-transparent transition-all">
                  <div className="space-y-4 pb-4">
                    {messages.map((msg, messageIndex) => {
                      const renderers: Components = {
                        code({ className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || "");
                          if (match && match[1] === "widget") {
                            try {
                              const widgetData = JSON.parse(
                                String(children).trim(),
                              );
                              if (widgetData.props?.hidden) {
                                return null;
                              }
                              switch (widgetData.type) {
                                case "button":
                                  return (
                                    <ButtonWidget
                                      {...widgetData.props}
                                      onButtonClick={(
                                        messageIndex,
                                        widgetIndex,
                                        action,
                                        label,
                                      ) =>
                                        handleButtonClick(
                                          messageIndex,
                                          widgetIndex,
                                          action,
                                          label,
                                        )
                                      }
                                      messageIndex={messageIndex}
                                    />
                                  );
                                case "flow":
                                  return (
                                    <div className="my-2">
                                      <FlowWidget
                                        mode="create"
                                        {...widgetData.props}
                                      />
                                    </div>
                                  );
                                default:
                                  return null;
                              }
                            } catch (error) {
                              console.error(
                                "Error parsing widget JSON:",
                                error,
                              );
                              return null;
                            }
                          }
                          return (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        },
                      };

                      return (
                        <div
                          key={messageIndex}
                          className={`flex ${
                            msg.sender === "user"
                              ? "justify-end"
                              : "justify-start"
                          }`}
                        >
                          <div
                            className={`max-w-11/12 rounded-3xl px-3 py-2 text-sm ${
                              msg.sender === "user"
                                ? "rounded-tr-none bg-chart-3 text-primary-foreground"
                                : "flex w-full flex-col gap-4"
                            }`}
                          >
                            {" "}
                            {msg.text && (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={renderers}
                              >
                                {msg.text}
                              </ReactMarkdown>
                            )}
                            {!!msg.widgets?.length && (
                              <div className="mt-2 flex flex-row flex-wrap justify-end gap-2">
                                {msg.widgets.map((widget, widgetIndex) => (
                                  <ReactMarkdown
                                    key={widgetIndex}
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      code: ({
                                        className,
                                        children,
                                        ...props
                                      }) => {
                                        const match = /language-(\w+)/.exec(
                                          className || "",
                                        );
                                        if (match && match[1] === "widget") {
                                          try {
                                            const widgetData = JSON.parse(
                                              String(children).trim(),
                                            );
                                            if (widgetData.props?.hidden) {
                                              return null;
                                            }
                                            switch (widgetData.type) {
                                              case "button":
                                                return (
                                                  <ButtonWidget
                                                    {...widgetData.props}
                                                    onButtonClick={
                                                      handleButtonClick
                                                    }
                                                    messageIndex={messageIndex}
                                                    widgetIndex={widgetIndex}
                                                  />
                                                );
                                              case "flow":
                                                return (
                                                  <div className="my-2">
                                                    <FlowWidget
                                                      mode="create"
                                                      {...widgetData.props}
                                                    />
                                                  </div>
                                                );
                                              default:
                                                return null;
                                            }
                                          } catch (error) {
                                            console.error(
                                              "Error parsing widget JSON:",
                                              error,
                                            );
                                            return null;
                                          }
                                        }
                                        return (
                                          <code
                                            className={className}
                                            {...props}
                                          >
                                            {children}
                                          </code>
                                        );
                                      },
                                    }}
                                  >
                                    {`\`\`\`widget\n${JSON.stringify(
                                      widget,
                                      null,
                                      2,
                                    )}\n\`\`\``}
                                  </ReactMarkdown>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              )}
              {isMinimized && showLastMessageInMinimized && (
                <ScrollArea type="scroll" className="flex-1 min-h-0 p-2">
                  <div className="p-2">
                    {[
                      isConnecting && !isBotResponding
                        ? lastUserMessage
                        : lastMessage,
                    ].map(
                      (msg, _messageIndex) =>
                        msg && (
                          <div
                            key={
                              msg === lastMessage
                                ? messages.length - 1
                                : "pending"
                            }
                            className={`flex ${
                              msg.sender === "user"
                                ? "justify-end"
                                : "justify-start"
                            }`}
                          >
                            <div
                              className={`max-w-11/12 rounded-3xl px-3 py-2 text-sm ${
                                msg.sender === "user"
                                  ? "rounded-tr-none bg-chart-3 text-primary-foreground"
                                  : "flex w-full flex-col gap-4"
                              }`}
                            >
                              {msg.text && (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {msg.text}
                                </ReactMarkdown>
                              )}
                              {!!msg.widgets?.length && (
                                <div className="mt-2 flex flex-row flex-wrap justify-end gap-2">
                                  {msg.widgets.map((widget, widgetIndex) => (
                                    <ReactMarkdown
                                      key={widgetIndex}
                                      remarkPlugins={[remarkGfm]}
                                      components={{
                                        code: ({
                                          className,
                                          children,
                                          ...props
                                        }) => {
                                          const match = /language-(\w+)/.exec(
                                            className || "",
                                          );
                                          if (match && match[1] === "widget") {
                                            try {
                                              const widgetData = JSON.parse(
                                                String(children).trim(),
                                              );
                                              if (widgetData.props?.hidden) {
                                                return null;
                                              }
                                              switch (widgetData.type) {
                                                case "button":
                                                  return (
                                                    <ButtonWidget
                                                      {...widgetData.props}
                                                      onButtonClick={
                                                        handleButtonClick
                                                      }
                                                      messageIndex={
                                                        messages.length - 1
                                                      }
                                                      widgetIndex={widgetIndex}
                                                    />
                                                  );
                                                case "flow":
                                                  return (
                                                    <div className="my-2">
                                                      <FlowWidget
                                                        mode="create"
                                                        {...widgetData.props}
                                                      />
                                                    </div>
                                                  );
                                                default:
                                                  return null;
                                              }
                                            } catch (error) {
                                              console.error(
                                                "Error parsing widget JSON:",
                                                error,
                                              );
                                              return null;
                                            }
                                          }
                                          return (
                                            <code
                                              className={className}
                                              {...props}
                                            >
                                              {children}
                                            </code>
                                          );
                                        },
                                      }}
                                    >
                                      {`\`\`\`widget\n${JSON.stringify(
                                        widget,
                                        null,
                                        2,
                                      )}\n\`\`\``}
                                    </ReactMarkdown>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                    )}
                  </div>
                </ScrollArea>
              )}
              <div className={`flex-none bg-background p-3 ${(!isMinimized && messages.length > 0) ? "border-t border-border" : ""}`}>
                <Textarea
                  className="text-md max-h-[200px] min-h-[20px] resize-none border-0 bg-transparent px-2 py-2 shadow-none focus-visible:ring-0"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      handleSendMessage(input);
                    }
                  }}
                  placeholder={
                    isConnecting ? "Connecting..." : "Type a message..."
                  }
                  disabled={isConnecting}
                />
                <div className="flex justify-between gap-1 px-2">
                  <div className="flex gap-1">
                    {messages.length > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => setIsMinimized(!isMinimized)}
                      >
                        {isMinimized ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <MicButton
                      onText={(text) => {
                        setInput((prev) => (prev ? prev + " " + text : text));
                      }}
                    />
                  </div>
                  <SpecialCTA
                    onClick={() => handleSendMessage(input)}
                    disabled={isConnecting || !input.trim()}
                    className="h-8 gap-1 px-3"
                  >
                    Send
                    <img
                      src="/buzooka-ai-icon.svg"
                      alt="Send"
                      className={`h-5 w-5 transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                        !(isConnecting || !input.trim())
                          ? "group-hover/special:scale-110 group-hover/special:rotate-[72deg] group-focus-visible/special:scale-110 group-focus-visible/special:rotate-[72deg]"
                          : ""
                      }`}
                    />
                  </SpecialCTA>
                </div>
                
              </div>
            </div>
            {(!!pinnedFlowWidget || chatProjects.length > 0) && (
              <div className="pt-6 -mt-6 flex flex-row shrink-0 bg-background/60 backdrop-blur-md rounded-xl border border-white/10 shadow-lg animate-in fade-in slide-in-from-top-2 overflow-hidden">
                
                
                {chatProjects.length > 0 && !showCreateForm && (
                  <div className="flex flex-col flex-1 gap-1 items-start text-left px-2 py-2 hover:bg-white/5 transition-colors">
                    <span className="text-[10px] font-bold text-muted-foreground/100">Projects created from this chat</span>
                    <div className="flex flex-wrap justify-center gap-1">
                      {chatProjects.map(p => (
                        <Link 
                          key={p.id} 
                          to={`/projects/${p.id}`}
                          className="px-3 py-1 rounded-lg bg-background/40 border border-border/40 text-[11px] font-medium hover:bg-background/80 border-primary/30 transition-all flex items-center gap-2 group/prj"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)] group-hover/prj:scale-110 transition-transform"></div>
                          {p.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                
                {!!pinnedFlowWidget && (
                  <div className={`px-4 py-3 flex flex-1 justify-center items-center ${chatProjects.length > 0 ? "border-b border-white/5" : ""}`}>
                    {!showCreateForm ? (
                      <Button 
                        onClick={() => {
                          setShowCreateForm(true);
                          setCreateProjectError(null);
                        }}
                        size="sm"
                        >
                        <Plus />New Project with this Architecture
                      </Button>
                    ) : (
                      <div className="flex flex-1 flex-col gap-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-muted-foreground/100 ml-1">New Project</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 px-2 text-[10px] hover:bg-destructive/10 hover:text-destructive transition-colors" 
                            onClick={() => {
                              setShowCreateForm(false);
                              setCreateProjectError(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-[9px] font-bold text-muted-foreground/50 ml-1 uppercase">Name</span>
                            <Input
                              placeholder="Project Name"
                              value={projectName}
                              onChange={(e) => setProjectName(e.target.value)}
                              className="h-8 border-none bg-background/40 text-[11px] shadow-sm focus-visible:ring-1 focus-visible:ring-primary/20 transition-all hover:bg-background/60"
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <span className="text-[9px] font-bold text-muted-foreground/50 ml-1 uppercase">Domain</span>
                            <Input
                              placeholder="Domain Name"
                              value={domainName}
                              onChange={(e) => setDomainName(e.target.value)}
                              className="h-8 border-none bg-background/40 text-[11px] shadow-sm focus-visible:ring-1 focus-visible:ring-primary/20 transition-all hover:bg-background/60"
                            />
                          </div>
                        </div>
                        {createProjectError && (
                          <p className="px-1 text-[10px] font-medium text-destructive animate-in slide-in-from-top-1">{createProjectError}</p>
                        )}
                        <Button
                          onClick={handleCreateProject}
                          disabled={creatingProject}
                          size="sm"
                          className="w-full h-8 text-xs font-bold shadow-md transition-all hover:shadow-lg active:scale-[0.98] bg-primary/90 hover:bg-primary"
                        >
                          {creatingProject ? "Creating Project..." : "Create Project"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
        {rightSidebar && <ChatHistory />}
      </div>
    </ChatContext.Provider>
  );
};
